import React, { useMemo } from 'react';
import { Calendar as CalendarIcon, Clock, Sparkles, Sun } from 'lucide-react';

// Her çalışma periyoduna tutarlı ve ayırt edici bir renk paleti atamak için sabit paletler
const PERIOD_PALETTES = [
    {
        bg: 'bg-blue-50/90 dark:bg-blue-950/40',
        border: 'border-blue-200 dark:border-blue-800/70',
        text: 'text-blue-900 dark:text-blue-200',
        subtext: 'text-blue-600 dark:text-blue-300',
        badge: 'bg-blue-600 text-white',
        dot: 'bg-blue-500',
        cellMini: 'bg-blue-500 text-white dark:bg-blue-600'
    },
    {
        bg: 'bg-indigo-50/90 dark:bg-indigo-950/40',
        border: 'border-indigo-200 dark:border-indigo-800/70',
        text: 'text-indigo-900 dark:text-indigo-200',
        subtext: 'text-indigo-600 dark:text-indigo-300',
        badge: 'bg-indigo-600 text-white',
        dot: 'bg-indigo-500',
        cellMini: 'bg-indigo-500 text-white dark:bg-indigo-600'
    },
    {
        bg: 'bg-teal-50/90 dark:bg-teal-950/40',
        border: 'border-teal-200 dark:border-teal-800/70',
        text: 'text-teal-900 dark:text-teal-200',
        subtext: 'text-teal-600 dark:text-teal-300',
        badge: 'bg-teal-600 text-white',
        dot: 'bg-teal-500',
        cellMini: 'bg-teal-500 text-white dark:bg-teal-600'
    },
    {
        bg: 'bg-purple-50/90 dark:bg-purple-950/40',
        border: 'border-purple-200 dark:border-purple-800/70',
        text: 'text-purple-900 dark:text-purple-200',
        subtext: 'text-purple-600 dark:text-purple-300',
        badge: 'bg-purple-600 text-white',
        dot: 'bg-purple-500',
        cellMini: 'bg-purple-500 text-white dark:bg-purple-600'
    },
    {
        bg: 'bg-cyan-50/90 dark:bg-cyan-950/40',
        border: 'border-cyan-200 dark:border-cyan-800/70',
        text: 'text-cyan-900 dark:text-cyan-200',
        subtext: 'text-cyan-600 dark:text-cyan-300',
        badge: 'bg-cyan-600 text-white',
        dot: 'bg-cyan-500',
        cellMini: 'bg-cyan-500 text-white dark:bg-cyan-600'
    }
];

const TEMP_PALETTE = {
    bg: 'bg-amber-50/95 dark:bg-amber-950/40',
    border: 'border-amber-300 dark:border-amber-700/70',
    text: 'text-amber-900 dark:text-amber-200',
    subtext: 'text-amber-700 dark:text-amber-300',
    badge: 'bg-amber-500 text-white',
    dot: 'bg-amber-500',
    cellMini: 'bg-amber-500 text-white dark:bg-amber-500'
};

const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
};

// Tarih ve atama geçmişini alıp ilgili güne düşen atamayı bulan yardımcı fonksiyon
// Backend PayrollService.GetAssignmentForDate ile %100 aynı tarih kırılım (Date-Split) önceliğine sahiptir.
export const getAssignmentForDate = (date, assignments) => {
    if (!Array.isArray(assignments) || assignments.length === 0) return null;

    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    const matching = assignments.filter(a => {
        const startDate = parseLocalDate(a.startDate);
        const endDate = parseLocalDate(a.endDate);
        if (!startDate || targetDate < startDate) return false;
        if (endDate && targetDate > endDate) return false;
        return true;
    });

    if (matching.length === 0) return null;

    matching.sort((a, b) => {
        const startDiff = parseLocalDate(b.startDate) - parseLocalDate(a.startDate);
        if (startDiff !== 0) return startDiff;
        const aHasEnd = a.endDate ? 1 : 0;
        const bHasEnd = b.endDate ? 1 : 0;
        return bHasEnd - aHasEnd;
    });

    return matching[0];
};

const resolveDayShiftGroup = (period, dayOfWeek) => {
    if (!period) return null;
    const dayMap = [
        period.sundayShiftGroup,
        period.mondayShiftGroup,
        period.tuesdayShiftGroup,
        period.wednesdayShiftGroup,
        period.thursdayShiftGroup,
        period.fridayShiftGroup,
        period.saturdayShiftGroup
    ];
    return dayMap[dayOfWeek] || null;
};

const MONTH_NAMES_TR = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

export default function CalendarView({ assignments = [], viewDate, mode = 'month', onSelectMonth }) {
    const daysOfWeek = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
    const daysOfWeekShort = ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz'];

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    const now = new Date();
    const todayYear = now.getFullYear();
    const todayMonth = now.getMonth();
    const todayDay = now.getDate();

    // Periyot ID -> Renk Paleti eşleşmesi
    const periodPaletteMap = useMemo(() => {
        const map = new Map();
        let idx = 0;
        assignments.forEach(a => {
            const pId = a.workPeriod?.id;
            if (pId && !map.has(pId)) {
                map.set(pId, PERIOD_PALETTES[idx % PERIOD_PALETTES.length]);
                idx++;
            }
        });
        return map;
    }, [assignments]);

    // Aylık istatistikler
    const monthStats = useMemo(() => {
        if (mode !== 'month') return null;
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        let workDays = 0;
        let offDays = 0;
        let tempDays = 0;
        let unassignedDays = 0;

        for (let d = 1; d <= daysInMonth; d++) {
            const cur = new Date(year, month, d);
            const a = getAssignmentForDate(cur, assignments);
            if (!a || !a.workPeriod) {
                unassignedDays++;
            } else {
                const grp = resolveDayShiftGroup(a.workPeriod, cur.getDay());
                if (grp) {
                    workDays++;
                    if (a.endDate) tempDays++;
                } else {
                    offDays++;
                }
            }
        }
        return { workDays, offDays, tempDays, unassignedDays };
    }, [assignments, year, month, mode]);

    // ========================
    //   YILLIK (12 AY) GÖRÜNÜM
    // ========================
    if (mode === 'year') {
        return (
            <div className="p-4 space-y-4">
                {/* Renk Açıklaması (Legend) */}
                <div className="flex flex-wrap items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl bg-slate-50 dark:bg-gray-800/70 border border-slate-200/70 dark:border-gray-700/60 text-xs">
                    <div className="flex flex-wrap items-center gap-4">
                        <span className="flex items-center gap-1.5 font-semibold text-gray-700 dark:text-gray-200">
                            <span className="w-3 h-3 rounded bg-blue-500 inline-block"></span>
                            Sürekli Vardiya (Çalışma)
                        </span>
                        <span className="flex items-center gap-1.5 font-semibold text-gray-700 dark:text-gray-200">
                            <span className="w-3 h-3 rounded bg-amber-500 inline-block"></span>
                            Geçici Vardiya
                        </span>
                        <span className="flex items-center gap-1.5 font-semibold text-gray-700 dark:text-gray-200">
                            <span className="w-3 h-3 rounded bg-emerald-100 dark:bg-emerald-900/50 border border-emerald-300 dark:border-emerald-700 inline-block"></span>
                            Hafta Tatili
                        </span>
                        <span className="flex items-center gap-1.5 font-semibold text-gray-500 dark:text-gray-400">
                            <span className="w-3 h-3 rounded bg-gray-100 dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 inline-block"></span>
                            Atama Yok
                        </span>
                    </div>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">
                        Detayını görmek istediğiniz ayın üzerine tıklayabilirsiniz
                    </span>
                </div>

                {/* 12 Ay Matrisi */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {MONTH_NAMES_TR.map((monthName, mIdx) => {
                        const firstDay = new Date(year, mIdx, 1);
                        const daysInM = new Date(year, mIdx + 1, 0).getDate();
                        let startDow = firstDay.getDay();
                        if (startDow === 0) startDow = 7;

                        const isCurrentMonth = year === todayYear && mIdx === todayMonth;
                        let mWorkCount = 0;
                        let mOffCount = 0;

                        const cells = [];
                        for (let i = 1; i < startDow; i++) {
                            cells.push(<div key={`empty-${mIdx}-${i}`} className="h-6" />);
                        }

                        for (let d = 1; d <= daysInM; d++) {
                            const curDate = new Date(year, mIdx, d);
                            const isToday = year === todayYear && mIdx === todayMonth && d === todayDay;
                            const assignment = getAssignmentForDate(curDate, assignments);

                            let cellClass = 'text-gray-400 dark:text-gray-600 bg-gray-50/70 dark:bg-gray-800/40';
                            let tooltip = `${d} ${monthName} ${year} — Vardiya Ataması Yok`;

                            if (assignment && assignment.workPeriod) {
                                const period = assignment.workPeriod;
                                const group = resolveDayShiftGroup(period, curDate.getDay());
                                const isTemp = Boolean(assignment.endDate);

                                if (group) {
                                    mWorkCount++;
                                    const palette = isTemp
                                        ? TEMP_PALETTE
                                        : (periodPaletteMap.get(period.id) || PERIOD_PALETTES[0]);
                                    cellClass = `${palette.cellMini} font-bold shadow-2xs`;
                                    tooltip = `${d} ${monthName} ${year}\nPeriyot: ${period.name}${isTemp ? ' (Geçici)' : ''}\nVardiya: ${group.name}`;
                                } else {
                                    mOffCount++;
                                    cellClass = 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 font-semibold border border-emerald-200/60 dark:border-emerald-800/50';
                                    tooltip = `${d} ${monthName} ${year}\nPeriyot: ${period.name}\nHafta Tatili`;
                                }
                            }

                            cells.push(
                                <div
                                    key={d}
                                    title={tooltip}
                                    className={`h-6 rounded-md flex items-center justify-center text-[11px] select-none transition-transform hover:scale-110 ${cellClass} ${
                                        isToday ? 'ring-2 ring-offset-1 ring-rose-500 dark:ring-offset-gray-900 z-10' : ''
                                    }`}
                                >
                                    {d}
                                </div>
                            );
                        }

                        return (
                            <div
                                key={monthName}
                                onClick={() => onSelectMonth && onSelectMonth(mIdx)}
                                className={`p-3.5 rounded-2xl border transition-all cursor-pointer hover:shadow-md group ${
                                    isCurrentMonth
                                        ? 'bg-blue-50/20 dark:bg-blue-950/15 border-blue-300 dark:border-blue-700/70'
                                        : 'bg-white dark:bg-gray-800/70 border-gray-200/80 dark:border-gray-700/60 hover:border-blue-300 dark:hover:border-blue-700'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-2.5 pb-1.5 border-b border-gray-100 dark:border-gray-700/60">
                                    <span className="font-bold text-sm text-gray-800 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors flex items-center gap-1.5">
                                        {monthName}
                                        {isCurrentMonth && (
                                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-bold">
                                                Bu Ay
                                            </span>
                                        )}
                                    </span>
                                    <div className="flex items-center gap-1.5 text-[10px] font-semibold">
                                        <span className="px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" title="Çalışma Günü">
                                            {mWorkCount} İş
                                        </span>
                                        <span className="px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300" title="Hafta Tatili">
                                            {mOffCount} Tatil
                                        </span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-gray-400 dark:text-gray-500 mb-1">
                                    {daysOfWeekShort.map(d => (
                                        <div key={d}>{d}</div>
                                    ))}
                                </div>

                                <div className="grid grid-cols-7 gap-1">
                                    {cells}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // ========================
    //   AYLIK DETAYLI GÖRÜNÜM
    // ========================
    const firstDayOfMonth = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let startDayOfWeek = firstDayOfMonth.getDay();
    if (startDayOfWeek === 0) startDayOfWeek = 7;

    const calendarDays = [];
    for (let i = 1; i < startDayOfWeek; i++) {
        calendarDays.push(
            <div
                key={`empty-start-${i}`}
                className="border border-gray-100/80 dark:border-gray-800/60 rounded-xl p-2 h-24 bg-gray-50/40 dark:bg-gray-900/20"
            />
        );
    }

    for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, month, day);
        const isToday = year === todayYear && month === todayMonth && day === todayDay;
        const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;
        const assignment = getAssignmentForDate(currentDate, assignments);

        let dayLabel = 'Atama Yok';
        let periodLabel = null;
        let shiftSummary = null;
        let cardClass = 'bg-gray-50/60 dark:bg-gray-900/30 border-dashed border-gray-200 dark:border-gray-800';
        let labelClass = 'text-gray-400 dark:text-gray-600 italic';
        let subLabelClass = 'text-gray-400 dark:text-gray-500';
        let dotClass = null;
        let isTemporary = false;
        let isOffDay = false;
        let hasAssignment = false;

        if (assignment && assignment.workPeriod) {
            hasAssignment = true;
            isTemporary = Boolean(assignment.endDate);
            const period = assignment.workPeriod;
            periodLabel = period.name;
            const group = resolveDayShiftGroup(period, currentDate.getDay());

            if (group) {
                dayLabel = group.name;
                if (Array.isArray(group.workShifts) && group.workShifts.length > 0) {
                    shiftSummary = group.workShifts.map(ws => ws.shortCode || ws.name).join(', ');
                }
                const palette = isTemporary
                    ? TEMP_PALETTE
                    : (periodPaletteMap.get(period.id) || PERIOD_PALETTES[0]);

                cardClass = `${palette.bg} ${palette.border} shadow-2xs hover:shadow-sm`;
                labelClass = `${palette.text} font-bold`;
                subLabelClass = palette.subtext;
                dotClass = palette.dot;
            } else {
                isOffDay = true;
                dayLabel = 'Hafta Tatili';
                cardClass = 'bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-200/70 dark:border-emerald-800/50';
                labelClass = 'text-emerald-700 dark:text-emerald-300 font-bold';
                subLabelClass = 'text-emerald-600/80 dark:text-emerald-400/70';
                dotClass = 'bg-emerald-500';
            }
        }

        calendarDays.push(
            <div
                key={day}
                title={
                    hasAssignment
                        ? `${day} ${MONTH_NAMES_TR[month]} ${year}\nPeriyot: ${periodLabel}${isTemporary ? ' (Geçici Atama)' : ''}\nDurum: ${dayLabel}${shiftSummary ? ` (${shiftSummary})` : ''}`
                        : `${day} ${MONTH_NAMES_TR[month]} ${year} — Vardiya ataması bulunmuyor`
                }
                className={`border rounded-xl p-2.5 h-24 flex flex-col justify-between transition-all duration-150 ${cardClass} ${
                    isToday ? 'ring-2 ring-indigo-600 dark:ring-indigo-400 shadow-md' : ''
                }`}
            >
                <div className="flex items-center justify-between gap-1">
                    <span
                        className={`text-xs font-extrabold leading-none ${
                            isToday
                                ? 'bg-indigo-600 text-white px-2 py-1 rounded-lg shadow-xs'
                                : isWeekend
                                    ? 'text-slate-600 dark:text-gray-300'
                                    : 'text-gray-800 dark:text-gray-100'
                        }`}
                    >
                        {day}
                    </span>

                    <div className="flex items-center gap-1">
                        {isToday && (
                            <span className="text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300">
                                Bugün
                            </span>
                        )}
                        {!isToday && isTemporary && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-200/80 dark:bg-amber-900/60 text-amber-900 dark:text-amber-200">
                                Geçici
                            </span>
                        )}
                        {!isToday && isOffDay && (
                            <Sun size={13} className="text-emerald-500 dark:text-emerald-400" />
                        )}
                    </div>
                </div>

                <div className="min-w-0 space-y-0.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                        {dotClass && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotClass}`} />}
                        <span className={`text-xs leading-tight truncate ${labelClass}`}>
                            {dayLabel}
                        </span>
                    </div>

                    {shiftSummary && (
                        <div className={`text-[10px] font-semibold truncate pl-3 ${subLabelClass}`}>
                            {shiftSummary}
                        </div>
                    )}

                    {periodLabel && !shiftSummary && (
                        <div className={`text-[10px] font-medium truncate pl-3 opacity-85 ${subLabelClass}`}>
                            {periodLabel}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 space-y-3.5">
            {/* Aylık Özet İstatistik Barı */}
            {monthStats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    <div className="flex items-center justify-between px-3.5 py-2 rounded-xl bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200/60 dark:border-blue-800/50">
                        <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 flex items-center gap-1.5">
                            <Clock size={14} /> Çalışma Günü
                        </span>
                        <span className="text-sm font-extrabold text-blue-900 dark:text-blue-100">{monthStats.workDays}</span>
                    </div>

                    <div className="flex items-center justify-between px-3.5 py-2 rounded-xl bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-800/50">
                        <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                            <Sun size={14} /> Hafta Tatili
                        </span>
                        <span className="text-sm font-extrabold text-emerald-900 dark:text-emerald-100">{monthStats.offDays}</span>
                    </div>

                    <div className="flex items-center justify-between px-3.5 py-2 rounded-xl bg-amber-50/70 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-800/50">
                        <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                            <Sparkles size={14} /> Geçici Vardiya
                        </span>
                        <span className="text-sm font-extrabold text-amber-900 dark:text-amber-100">{monthStats.tempDays}</span>
                    </div>

                    <div className="flex items-center justify-between px-3.5 py-2 rounded-xl bg-gray-100/80 dark:bg-gray-800/60 border border-gray-200/70 dark:border-gray-700/60">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                            <CalendarIcon size={14} /> Atanmamış
                        </span>
                        <span className="text-sm font-extrabold text-gray-800 dark:text-gray-200">{monthStats.unassignedDays}</span>
                    </div>
                </div>
            )}

            {/* Haftanın Günleri Başlığı */}
            <div className="grid grid-cols-7 gap-2 text-center font-bold text-xs py-1.5 rounded-lg bg-slate-100/80 dark:bg-gray-800/80 text-slate-600 dark:text-gray-300 uppercase tracking-wider">
                {daysOfWeek.map(day => (
                    <div key={day}>{day}</div>
                ))}
            </div>

            {/* Takvim Hücreleri */}
            <div className="grid grid-cols-7 gap-2">
                {calendarDays}
            </div>
        </div>
    );
}
